import { useMemo, useState, type FormEvent } from 'react';
import {
  eyebrowStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  pageStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { staffRepoFor } from '../lib/repositories/outlet-repos.js';
import { callInviteStaff } from '../lib/callables/staff.js';
import { OutletSwitcher } from '../components/outlet-switcher.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX, dashboardPageStyle } from '../lib/layout.js';
import { phone as phoneUtil, type StaffDoc, type StaffRole } from '@kilat/shared';

/**
 * /staff — per-outlet staff roster + invite form. Owner / manager can:
 *   - See everyone on this outlet
 *   - Invite a new staff member (phone + role + display name)
 *   - Change someone's role
 *   - Deactivate someone (sets active=false; claimsSyncStaff revokes the claim)
 *
 * Add/edit/deactivate writes go directly through the repo (firestore.rules
 * gates to owner|manager). The `inviteStaff` callable is only needed
 * for the cross-resource part of fresh invites: look up by phone OR
 * create a new phone-only auth user.
 *
 * Multi-outlet note: staff are per-outlet. If you want the same cashier
 * at outlets A AND B, you invite them twice (once per outlet). The
 * `claimsSyncStaff` trigger aggregates roles across all outlets into
 * one `roles[tenantId]` claim using highest-privilege-wins, so the
 * cashier doesn't see a confusing per-outlet claim split.
 *
 * Per [feedback-no-dead-ends]: every state has explicit copy.
 */

const INVITABLE_ROLES: readonly StaffRole[] = ['manager', 'cashier', 'washer'];

export function StaffPage() {
  const { user, claims, loading: authLoading } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const tenantRole =
    tenantId &&
    typeof (claims as { roles?: Record<string, string> })?.roles?.[tenantId] === 'string'
      ? ((claims as { roles: Record<string, string> }).roles[tenantId] as StaffRole)
      : null;
  const canManage = tenantRole === 'owner' || tenantRole === 'manager';

  const { outletId, loading: outletLoading } = useActiveOutlet();

  const [reloadTick, setReloadTick] = useState(0);
  const subscribe = useMemo(() => {
    if (!tenantId || !outletId) return null;
    const repo = staffRepoFor(tenantId, outletId);
    return (cb: (vs: StaffDoc[]) => void) => repo.listSnapshot(cb);
  }, [tenantId, outletId]);

  const {
    data: staff,
    loading: staffLoading,
    error,
  } = useTrackedSnapshot<StaffDoc[]>(['staff', tenantId, outletId, reloadTick], subscribe);

  if (authLoading) return <Splash label={t.t('dashboard.staff.loadingStaff')} />;
  if (!user) return <Splash label={t.t('dashboard.staff.signInPrompt')} />;
  if (!tenantId) return <Splash label={t.t('dashboard.states.loadingAccount')} />;
  if (outletLoading) return <Splash label={t.t('dashboard.staff.loadingOutlet')} />;
  if (!outletId)
    return <Splash label={t.t('dashboard.staff.pickOutlet')} action={<OutletSwitcher />} />;
  if (!canManage)
    return (
      <Splash
        label={t.t('dashboard.staff.notManager')}
        action={
          <Link to="/queue" style={ghostLinkStyle}>
            {t.t('dashboard.states.backToQueue')}
          </Link>
        }
      />
    );

  return (
    <main style={dashboardPageStyle}>
      <Header />
      <section
        style={{
          maxWidth: PAGE_FRAME_MAX,
          margin: '0 auto',
          padding: '0 var(--space-5) var(--space-12) var(--space-5)',
          display: 'grid',
          gap: 'var(--space-4)',
        }}
      >
        {error ? (
          <ErrorCard message={error.message} onRetry={() => setReloadTick((n) => n + 1)} />
        ) : staffLoading || !staff ? (
          <SkeletonStaff />
        ) : (
          // The team on the left, the invite form beside it — stacked on a phone.
          <div className="layout-split-aside">
            <StaffList
              staff={staff}
              tenantId={tenantId}
              outletId={outletId}
              currentUid={user.uid}
            />
            <InviteCard
              tenantId={tenantId}
              outletId={outletId}
              existingPhones={new Set(staff.map((s) => s.phone).filter(Boolean) as string[])}
            />
          </div>
        )}
      </section>
    </main>
  );
}

// ── header ──────────────────────────────────────────────────────────────

function Header() {
  const t = useI18n();
  return (
    <header
      style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        marginBottom: 'var(--space-6)',
      }}
    >
      <div
        style={{
          maxWidth: PAGE_FRAME_MAX,
          margin: '0 auto',
          padding: 'var(--space-5)',
          display: 'flex',
          gap: 'var(--space-4)',
          alignItems: 'center',
          flexWrap: 'wrap',
          minWidth: 0,
        }}
      >
        <div>
          <p style={eyebrowStyle}>{t.t('dashboard.staff.eyebrow')}</p>
          <h1
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-h1)',
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            {t.t('dashboard.staff.title')}
          </h1>
        </div>
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
          }}
        >
          <Link to="/queue" style={ghostLinkStyle}>
            {t.t('dashboard.states.backToQueue')}
          </Link>
        </div>
      </div>
    </header>
  );
}

// ── staff list ──────────────────────────────────────────────────────────

function StaffList({
  staff,
  tenantId,
  outletId,
  currentUid,
}: {
  readonly staff: readonly (StaffDoc & { id?: string })[];
  readonly tenantId: string;
  readonly outletId: string;
  readonly currentUid: string;
}) {
  const t = useI18n();
  const sorted = staff.slice().sort((a, b) => roleOrder(a.role) - roleOrder(b.role));

  return (
    <section style={sectionStyle}>
      <header style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t.t('dashboard.staff.currentStaff', {
            count: staff.filter((s) => s.active).length,
          })}
        </h2>
        <p style={metaStyle}>{t.t('dashboard.staff.rolesHint')}</p>
      </header>
      <ul
        style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-2)' }}
      >
        {sorted.map((s) => (
          <StaffRow
            key={s.id ?? s.phone}
            staff={s}
            tenantId={tenantId}
            outletId={outletId}
            isSelf={s.id === currentUid}
          />
        ))}
      </ul>
    </section>
  );
}

function StaffRow({
  staff,
  tenantId,
  outletId,
  isSelf,
}: {
  readonly staff: StaffDoc & { id?: string };
  readonly tenantId: string;
  readonly outletId: string;
  readonly isSelf: boolean;
}) {
  const t = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function updateRole(nextRole: StaffRole) {
    if (!staff.id) return;
    setBusy(true);
    setError(null);
    try {
      await staffRepoFor(tenantId, outletId).update(staff.id, { role: nextRole });
      logger.info('staff role updated', { uid: staff.id, nextRole });
    } catch (e) {
      setError((e as Error).message ?? t.t('dashboard.staff.updateFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function setActive(next: boolean) {
    if (!staff.id) return;
    setBusy(true);
    setError(null);
    try {
      await staffRepoFor(tenantId, outletId).update(staff.id, { active: next });
      logger.info('staff active updated', { uid: staff.id, active: next });
    } catch (e) {
      setError((e as Error).message ?? t.t('dashboard.staff.updateFailed'));
    } finally {
      setBusy(false);
    }
  }

  const isOwner = staff.role === 'owner';
  return (
    <li
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: staff.active ? 'var(--tint-subtle)' : 'var(--tint-subtle)',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 'var(--space-3)',
        alignItems: 'center',
        opacity: staff.active ? 1 : 0.65,
      }}
    >
      <div>
        <p style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 500 }}>
          {staff.displayName ?? t.t('dashboard.staff.noNameYet')}
          {isSelf ? <SelfPill /> : null}
          {!staff.active ? <InactivePill /> : null}
        </p>
        <p style={metaStyle}>
          {staff.phone ? phoneUtil.formatE164(staff.phone) : t.t('dashboard.staff.noPhone')}
          {' · '}
          <RolePill role={staff.role} />
        </p>
        {error ? (
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-accent)',
            }}
          >
            {error}
          </p>
        ) : null}
      </div>
      <div
        style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}
      >
        {isOwner || isSelf ? (
          /* Owner cannot be edited via this page (their role is set at signup).
             Self-edit is intentionally disabled to prevent operators from
             accidentally locking themselves out of their own dashboard. */
          <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
            {isOwner ? t.t('dashboard.staff.ownerNotEditable') : t.t('dashboard.staff.youPill')}
          </span>
        ) : (
          <>
            <select
              value={staff.role}
              onChange={(e) => updateRole(e.target.value as StaffRole)}
              disabled={busy}
              style={selectStyle}
            >
              {INVITABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {t.t(`dashboard.staff.roles.${r}`)}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setActive(!staff.active)}
              disabled={busy}
              style={staff.active ? ghostButtonStyle : primaryButtonStyle}
            >
              {staff.active ? t.t('dashboard.staff.deactivate') : t.t('dashboard.staff.reactivate')}
            </button>
          </>
        )}
      </div>
    </li>
  );
}

// ── invite card ────────────────────────────────────────────────────────

function InviteCard({
  tenantId,
  outletId,
  existingPhones,
}: {
  readonly tenantId: string;
  readonly outletId: string;
  readonly existingPhones: Set<string>;
}) {
  const t = useI18n();
  const brand = usePlatformBrand();
  const [phone, setPhone] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<StaffRole>('cashier');
  const [stage, setStage] = useState<
    | { kind: 'idle' }
    | { kind: 'submitting' }
    | { kind: 'success'; message: string }
    | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  const normalisedPhone = useMemo(() => {
    if (!phone.trim()) return null;
    return phoneUtil.normaliseMalaysianPhone(phone);
  }, [phone]);

  const phoneTone: 'muted' | 'success' | 'warn' =
    phone.trim().length === 0 ? 'muted' : normalisedPhone ? 'success' : 'warn';
  const phoneHint =
    phone.trim().length === 0
      ? t.t('dashboard.staff.invite.phoneHintDefault')
      : normalisedPhone
        ? t.t('dashboard.staff.invite.phoneHintValid', {
            phone: phoneUtil.formatE164(normalisedPhone),
          })
        : t.t('dashboard.staff.invite.phoneHintInvalid');
  const alreadyOnTeam = normalisedPhone !== null && existingPhones.has(normalisedPhone);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!normalisedPhone) {
      setStage({ kind: 'failed', message: t.t('dashboard.staff.invite.errNoPhone') });
      return;
    }
    if (alreadyOnTeam) {
      setStage({ kind: 'failed', message: t.t('dashboard.staff.invite.errAlreadyTeam') });
      return;
    }
    setStage({ kind: 'submitting' });
    try {
      const result = await callInviteStaff({
        tenantId,
        outletId,
        phone: normalisedPhone,
        role,
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      });
      logger.info('staff invited', { uid: result.uid, userCreated: result.userCreated, role });
      setStage({
        kind: 'success',
        message: result.userCreated
          ? t.t('dashboard.staff.invite.successCreated', {
              phone: phoneUtil.formatE164(normalisedPhone),
            })
          : t.t('dashboard.staff.invite.successExisting', {
              phone: phoneUtil.formatE164(normalisedPhone),
              brand: brand.name,
            }),
      });
      setPhone('');
      setDisplayName('');
      setRole('cashier');
    } catch (err) {
      setStage({
        kind: 'failed',
        message: (err as Error).message ?? t.t('dashboard.staff.invite.errFailed'),
      });
    }
  }

  return (
    <section style={sectionStyle}>
      <header style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t.t('dashboard.staff.inviteTitle')}
        </h2>
        <p style={metaStyle}>{t.t('dashboard.staff.inviteSubtitle')}</p>
      </header>
      <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <Field label={t.t('dashboard.staff.invite.phoneLabel')} hint={phoneHint} tone={phoneTone}>
          <input
            style={inputStyle}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t.t('dashboard.staff.phonePlaceholder')}
            autoComplete="tel"
            required
          />
        </Field>
        <Field label={t.t('dashboard.staff.invite.displayNameLabel')}>
          <input
            style={inputStyle}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t.t('dashboard.staff.invite.displayNamePlaceholder')}
            autoComplete="off"
          />
        </Field>
        <Field label={t.t('dashboard.staff.invite.roleLabel')}>
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            {INVITABLE_ROLES.map((r) => (
              <label
                key={r}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: 'var(--space-2)',
                  cursor: 'pointer',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: role === r ? 'rgba(13,122,79,0.04)' : 'transparent',
                }}
              >
                <input
                  type="radio"
                  name="role"
                  checked={role === r}
                  onChange={() => setRole(r)}
                  style={{ accentColor: 'var(--color-primary)', marginTop: 4 }}
                />
                <div>
                  <span style={{ fontWeight: 500 }}>{t.t(`dashboard.staff.roles.${r}`)}</span>
                  <p style={{ ...metaStyle, marginTop: 2 }}>
                    {t.t(`dashboard.staff.roleDescriptions.${r}`)}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </Field>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <button
            type="submit"
            disabled={stage.kind === 'submitting' || !normalisedPhone || alreadyOnTeam}
            style={{
              ...primaryButtonStyle,
              opacity: stage.kind === 'submitting' || !normalisedPhone || alreadyOnTeam ? 0.5 : 1,
              cursor: stage.kind === 'submitting' ? 'wait' : 'pointer',
            }}
          >
            {stage.kind === 'submitting'
              ? t.t('dashboard.staff.invite.submitting')
              : t.t('dashboard.staff.invite.submit')}
          </button>
          {stage.kind === 'success' ? (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-primary)' }}>
              {stage.message}
            </span>
          ) : null}
          {stage.kind === 'failed' ? (
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-accent)' }}>
              {stage.message}
            </span>
          ) : null}
        </div>
      </form>
    </section>
  );
}

// ── primitives ─────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  tone = 'muted',
  children,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly tone?: 'muted' | 'success' | 'warn';
  readonly children: React.ReactNode;
}) {
  const hintColor =
    tone === 'success'
      ? 'var(--color-primary)'
      : tone === 'warn'
        ? '#b45309'
        : 'var(--color-on-surface-muted)';
  return (
    <label style={{ display: 'grid', gap: 'var(--space-1)' }}>
      <span
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
      {hint ? (
        <span
          style={{ fontSize: 'var(--text-meta)', color: hintColor, lineHeight: 1.4 }}
          aria-live="polite"
        >
          {hint}
        </span>
      ) : null}
    </label>
  );
}

function RolePill({ role }: { readonly role: StaffRole }) {
  const t = useI18n();
  const palette: Record<StaffRole, { bg: string; fg: string }> = {
    owner: { bg: 'rgba(13,122,79,0.12)', fg: 'var(--color-primary)' },
    manager: { bg: 'rgba(59,130,246,0.12)', fg: '#1d4ed8' },
    cashier: { bg: 'rgba(245,158,11,0.12)', fg: '#b45309' },
    washer: { bg: 'var(--color-border)', fg: 'var(--color-on-surface-muted)' },
  };
  const v = palette[role];
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: v.bg,
        color: v.fg,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
      }}
    >
      {t.t(`dashboard.staff.roles.${role}`)}
    </span>
  );
}

function InactivePill() {
  const t = useI18n();
  return (
    <span
      style={{
        marginLeft: 'var(--space-2)',
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--color-border)',
        color: 'var(--color-on-surface-muted)',
        fontSize: '0.6875rem',
        fontWeight: 600,
      }}
    >
      {t.t('dashboard.staff.inactivePill')}
    </span>
  );
}

function SelfPill() {
  const t = useI18n();
  return (
    <span
      style={{
        marginLeft: 'var(--space-2)',
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: 'rgba(13,122,79,0.12)',
        color: 'var(--color-primary)',
        fontSize: '0.6875rem',
        fontWeight: 600,
      }}
    >
      {t.t('dashboard.staff.youPill')}
    </span>
  );
}

function SkeletonStaff() {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 80,
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-border)',
          }}
        />
      ))}
    </div>
  );
}

function ErrorCard({
  message,
  onRetry,
}: {
  readonly message: string;
  readonly onRetry?: () => void;
}) {
  const t = useI18n();
  return (
    <article
      style={{ ...sectionStyle, display: 'grid', gap: 'var(--space-3)', justifyItems: 'start' }}
    >
      <p style={{ margin: 0, color: 'var(--color-accent)' }}>
        {t.t('dashboard.staff.loadError', { message })}
      </p>
      {onRetry ? (
        <button type="button" onClick={onRetry} style={primaryButtonStyle}>
          {t.t('common.tryAgain')}
        </button>
      ) : null}
    </article>
  );
}

function Splash({ label, action }: { readonly label: string; readonly action?: React.ReactNode }) {
  return (
    <main style={pageStyle}>
      <section
        style={{
          maxWidth: 480,
          margin: 'var(--space-8) auto',
          padding: 'var(--space-6)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-surface)',
          textAlign: 'center',
          display: 'grid',
          gap: 'var(--space-4)',
          justifyItems: 'center',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {label}
        </p>
        {action}
      </section>
    </main>
  );
}

// ── helpers ────────────────────────────────────────────────────────────

function roleOrder(role: StaffRole): number {
  switch (role) {
    case 'owner':
      return 0;
    case 'manager':
      return 1;
    case 'cashier':
      return 2;
    case 'washer':
      return 3;
  }
}

// ── styles ─────────────────────────────────────────────────────────────

const sectionStyle: React.CSSProperties = {
  padding: 'var(--space-5)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
};

const selectStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border-strong)',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};

const metaStyle: React.CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};
